import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useConsumeMagicLinkMutation } from '../../features/auth/authApi';
import { getRoleHomePath } from '../../routes/roleHome';
import { authErrorMessage } from '../../lib/authErrorMessage';
import Button from '../../components/ui/Button';

/**
 * Landing page for the link emailed by POST /auth/magic-link. Single-use —
 * the token is invalidated on first use whether or not it succeeds, so
 * this fires the consume call exactly once on mount (StrictMode-safe via
 * a ref guard) rather than on any user action.
 *
 * Route path note: confirmed from an actual emailed link that the backend's
 * mail template points to /auth/magic-link?token=... — that's the primary
 * route this page is mounted at (see AppRoutes.jsx). /magic-link/consume
 * was this app's original guess before that was confirmed and is kept
 * registered too as a harmless alias.
 */
export default function MagicLinkConsume() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [consumeMagicLink] = useConsumeMagicLinkMutation();
  const [status, setStatus] = useState('pending'); // pending | error
  const [message, setMessage] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!token) {
      setStatus('error');
      setMessage('This link is missing its sign-in token.');
      return;
    }

    consumeMagicLink({ token })
      .unwrap()
      .then((result) => {
        const roles = result?.user?.roles ?? result?.user?.role ?? [];
        navigate(getRoleHomePath(roles), { replace: true });
      })
      .catch((err) => {
        setStatus('error');
        setMessage(authErrorMessage(err, { fallback: 'This link is invalid or has expired.' }));
      });
  }, [token, consumeMagicLink, navigate]);

  if (status === 'error') {
    return (
      <div className="text-center">
        <p className="text-4xl mb-3">⚠️</p>
        <h1 className="text-xl font-semibold text-foreground mb-2">Link didn't work</h1>
        <p className="text-sm text-foreground-muted mb-6">{message}</p>
        <Link to="/login"><Button className="w-full">Back to login</Button></Link>
      </div>
    );
  }

  return (
    <div className="text-center py-8">
      <div className="h-8 w-8 mx-auto animate-spin rounded-full border-2 border-accent border-t-transparent mb-4" />
      <p className="text-sm text-foreground-muted">Signing you in…</p>
    </div>
  );
}
